import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import ConfirmDialog from "../../components/ConfirmDialog";
import { isBlank } from "../../util/ext";
import Paper from "@material-ui/core/Paper";
import { useConfirm } from "../../common/use-confirm";
import {
  append,
  difference,
  filter,
  includes,
  pluck,
  prop,
  sortBy,
  without,
} from "ramda";
import Grid from "@material-ui/core/Grid";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import { useRoles } from "../../user-admin/roleServices";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import { useOrganizationRolesAdd } from "../organizationServices";
import { useErrorBox } from "./use-errorBox";
import DialogContentText from "@material-ui/core/DialogContentText";
import { formatRoleLabel } from "../../util/stringFormat";

const useStyles = makeStyles(theme => ({
  form: {
    marginTop: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const OrganizationRoleDialog = ({
  open,
  onCancel,
  onSave,
  organizationId,
  organizationRoles,
}) => {
  const classes = useStyles();
  const { setGeneralError, ErrorBox: DialogErrors } = useErrorBox();

  //-- Manage the confirm dialog
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;

  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);

  const rolesResponse = useRoles();
  useEffect(() => {
    console.log("roles setup useEffect...");
    if (rolesResponse.isSuccess) {
      setAvailableRoles(
        sortBy(
          prop("roleName"),
          filter(
            r => r.roleType === "ORGANIZATION",
            difference(rolesResponse.data, organizationRoles),
          ),
        ),
      );
    }
  }, [rolesResponse.isSuccess, rolesResponse.data, organizationRoles]);

  const addOrganizationRoles = useOrganizationRolesAdd();
  const handleApply = async () => {
    const roleList = pluck("roleName", selectedRoles);
    try {
      await addOrganizationRoles.mutateAsync({
        resourceIds: { orgId: organizationId },
        data: roleList,
      });
      setSelectedRoles([]);
      onSave(roleList);
    } catch (err) {
      setGeneralError(err);
    }
  };

  const handleClose = useCallback(() => {
    if (isBlank(selectedRoles)) {
      onCancel();
    } else {
      showConfirm(
        "Discard Changes?",
        "You have selected roles without applying.  Do you wish to discard these changes?",
        {
          onConfirm: () => {
            onCancel();
          },
        },
      );
    }
  }, [selectedRoles, showConfirm, onCancel]);

  const handleRoleSelected = role => event => {
    if (event.target.checked) {
      setSelectedRoles(prev => append(role, prev));
    } else {
      setSelectedRoles(prev => without([role], selectedRoles));
    }
  };

  return (
    <Dialog open={open}>
      <DialogTitle id="add-organization-roles-dialog-title">
        Add Organization Roles
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Check the Role that should be added to the Organization.
        </DialogContentText>
        <DialogErrors />
        <Paper>
          <Box className={classes.form}>
            <Grid spacing={2} container>
              {availableRoles.map(role => (
                <Grid key={role.roleId} item md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includes(role, selectedRoles)}
                        onChange={handleRoleSelected(role)}
                        name={role.roleName}
                        color="primary"
                      />
                    }
                    label={formatRoleLabel(role.roleName)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Paper>
        <ConfirmDialog handler={confirmHandler} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrganizationRoleDialog;
